import type { ModelAssetNode } from '../assets/model-asset-types'
import { listAssemblyDocumentDirectChildNodes } from '../assembly-document/assembly-document-operations'
import type { AssemblyDocument } from '../assembly-document/assembly-document-types'
import { resolveRepositoryPuppyNodeOccurrenceIdentity } from '../assembly-document/resolve-repository-puppy-node-occurrence-identity'

/**
 * 外部一级 child 与仓库 Puppy occurrence 的显式桥接。
 */
export interface MaterializedPuppyRootSourceChildBridge {
  /** 外部一级 child 的结构 key，仅用于定位待替换槽位。 */
  readonly sourceNodeKey: string
  /** node_where 明确指向的仓库 Puppy 包路径。 */
  readonly packageFileId: string
  /** 从 node_where 恢复的包内持久化装配节点 ID。 */
  readonly assemblyNodeId: string
}

/**
 * 从装配工作文档创建当前 Puppy 包可证明的一级 occurrence 桥接。
 *
 * 该函数只消费严格 `node_where + packageFileId` 解析结果；无法证明属于当前包的 direct child
 * 不会进入桥接集合，后续合并仍将其视为真实外部节点。
 *
 * @param input 装配文档、根结构 key 与当前 Puppy 包路径。
 * @returns 可直接交给物化合并函数的显式桥接记录。
 */
export function createMaterializedPuppyRootSourceChildBridges(input: {
  readonly document: AssemblyDocument
  readonly rootNodeKey: string
  readonly packageFileId: string
}): readonly MaterializedPuppyRootSourceChildBridge[] {
  return listAssemblyDocumentDirectChildNodes(input.document, input.rootNodeKey)
    .flatMap((child) => {
      const identity = resolveRepositoryPuppyNodeOccurrenceIdentity({
        nodeWhere: child.nodeWhere,
        packageFileId: input.packageFileId,
      })
      return identity.ok
        ? [{
            sourceNodeKey: child.nodeKey,
            packageFileId: input.packageFileId,
            assemblyNodeId: identity.assemblyNodeId,
          }]
        : []
    })
}

/**
 * 为 embedded puppy occurrence 创建完整 provenance key。
 *
 * @param child 待识别的模型节点。
 * @returns embedded 节点的容器、文件、assemblyNodeId 组合键；外部节点返回 null。
 */
function createEmbeddedPuppyOccurrenceProvenanceKey(child: ModelAssetNode): string | null {
  const source = child.assetSource
  return source?.kind === 'embedded-puppy-occurrence'
    ? JSON.stringify([
        source.containerNodeKey,
        source.containerFile.fileId,
        source.assemblyNodeId,
      ])
    : null
}

/**
 * 生成重复 embedded provenance 的完整诊断上下文。
 *
 * @param child 当前发现的重复节点。
 * @param firstNodeKey 首次占用同一 provenance 的节点 key。
 * @returns 包含容器、文件、assemblyNodeId 与冲突节点 key 的诊断文本。
 */
function describeDuplicateEmbeddedPuppyOccurrenceProvenance(
  child: ModelAssetNode,
  firstNodeKey: string,
): string {
  const source = child.assetSource
  if (source?.kind !== 'embedded-puppy-occurrence') {
    throw new Error(`embedded provenance 校验收到非 embedded 节点：nodeKey=${child.nodeKey}`)
  }
  return `containerNodeKey=${source.containerNodeKey}, packageFileId=${source.containerFile.fileId}, `
    + `assemblyNodeId=${source.assemblyNodeId}, firstNodeKey=${firstNodeKey}, duplicateNodeKey=${child.nodeKey}`
}

/**
 * 按 embedded provenance 把新 project-model 根直系节点合并回原 children 槽位。
 *
 * 规则：同容器、同包、同 assemblyNodeId 的旧 embedded child 在原槽位替换；旧 embedded
 * 已不存在时移除；所有外部 child 原对象、原相对顺序保留；剩余新 embedded 按 project-model
 * 顺序追加。禁止使用 nodeKey、instanceUuid、名称或兄弟序号推断身份。
 *
 * @param input 原 children、新物化 children 与当前容器身份。
 * @returns provenance 合并后的根直系 children。
 * @throws 当已有或新物化节点的 embedded provenance 重复时。
 * @throws 当 source child bridge 非法，或其 nodeKey、occurrence 身份重复时。
 * @throws 当新物化节点无法创建 embedded provenance key 时。
 */
export function mergeMaterializedPuppyRootChildrenByProvenance(input: {
  readonly sourceChildren: readonly ModelAssetNode[]
  readonly materializedChildren: readonly ModelAssetNode[]
  readonly containerNodeKey: string
  readonly packageFileId: string
  /** 已由装配文档严格解析的外部一级 child 桥接；缺失时保持原合并行为。 */
  readonly sourceChildBridges?: readonly MaterializedPuppyRootSourceChildBridge[]
}): readonly ModelAssetNode[] {
  const materializedChildByProvenance = new Map<string, ModelAssetNode>()
  for (const child of input.materializedChildren) {
    const provenanceKey = createEmbeddedPuppyOccurrenceProvenanceKey(child)
    if (!provenanceKey) {
      continue
    }
    const firstChild = materializedChildByProvenance.get(provenanceKey)
    if (firstChild) {
      throw new Error(
        'project-model embedded provenance 重复：'
        + describeDuplicateEmbeddedPuppyOccurrenceProvenance(child, firstChild.nodeKey),
      )
    }
    materializedChildByProvenance.set(provenanceKey, child)
  }

  const currentBridgeBySourceNodeKey = new Map<string, MaterializedPuppyRootSourceChildBridge>()
  const sourceNodeKeyByBridgedAssemblyNodeId = new Map<string, string>()
  for (const bridge of input.sourceChildBridges ?? []) {
    if (bridge.packageFileId !== input.packageFileId) {
      continue
    }
    const sourceNodeKey = bridge.sourceNodeKey.trim()
    const assemblyNodeId = bridge.assemblyNodeId.trim()
    if (!sourceNodeKey || !assemblyNodeId) {
      throw new Error(
        `source child bridge 非法：packageFileId=${input.packageFileId}, sourceNodeKey=${bridge.sourceNodeKey}, assemblyNodeId=${bridge.assemblyNodeId}`,
      )
    }
    if (currentBridgeBySourceNodeKey.has(sourceNodeKey)) {
      throw new Error(
        `source child bridge nodeKey 重复：packageFileId=${input.packageFileId}, sourceNodeKey=${sourceNodeKey}`,
      )
    }
    const firstSourceNodeKey = sourceNodeKeyByBridgedAssemblyNodeId.get(assemblyNodeId)
    if (firstSourceNodeKey) {
      throw new Error(
        `source child bridge occurrence 重复：packageFileId=${input.packageFileId}, assemblyNodeId=${assemblyNodeId}, `
        + `firstNodeKey=${firstSourceNodeKey}, duplicateNodeKey=${sourceNodeKey}`,
      )
    }
    currentBridgeBySourceNodeKey.set(sourceNodeKey, bridge)
    sourceNodeKeyByBridgedAssemblyNodeId.set(assemblyNodeId, sourceNodeKey)
  }

  const firstSourceChildByProvenance = new Map<string, ModelAssetNode>()
  const consumedMaterializedProvenances = new Set<string>()
  const mergedChildren: ModelAssetNode[] = []

  for (const sourceChild of input.sourceChildren) {
    const source = sourceChild.assetSource
    if (source?.kind !== 'embedded-puppy-occurrence') {
      const bridge = currentBridgeBySourceNodeKey.get(sourceChild.nodeKey)
      if (!bridge) {
        // 没有当前包显式桥接的节点属于真实外部状态，必须保留原对象和原槽位。
        mergedChildren.push(sourceChild)
        continue
      }

      const bridgedProvenanceKey = JSON.stringify([
        input.containerNodeKey,
        input.packageFileId,
        bridge.assemblyNodeId,
      ])
      const bridgedMaterializedChild = materializedChildByProvenance.get(bridgedProvenanceKey)
      if (!bridgedMaterializedChild) {
        // 多 pass 补树中 bridge 可能先于包内 occurrence 出现；保留外部节点供后续 RPC 补齐。
        mergedChildren.push(sourceChild)
        continue
      }

      consumedMaterializedProvenances.add(bridgedProvenanceKey)
      mergedChildren.push(bridgedMaterializedChild)
      continue
    }

    const sourceProvenanceKey = createEmbeddedPuppyOccurrenceProvenanceKey(sourceChild)
    if (!sourceProvenanceKey) {
      throw new Error(`embedded provenance key 创建失败：nodeKey=${sourceChild.nodeKey}`)
    }
    const firstSourceChild = firstSourceChildByProvenance.get(sourceProvenanceKey)
    if (firstSourceChild) {
      throw new Error(
        'source embedded provenance 重复：'
        + describeDuplicateEmbeddedPuppyOccurrenceProvenance(sourceChild, firstSourceChild.nodeKey),
      )
    }
    firstSourceChildByProvenance.set(sourceProvenanceKey, sourceChild)

    const isCurrentEmbedded = source.containerNodeKey === input.containerNodeKey
      && source.containerFile.fileId === input.packageFileId
    if (!isCurrentEmbedded) {
      // 旧 container/package 的 embedded 节点同样属于旧包内物化状态，重物化时直接移除。
      continue
    }

    const replacement = materializedChildByProvenance.get(sourceProvenanceKey)
    if (!replacement) {
      continue
    }

    consumedMaterializedProvenances.add(sourceProvenanceKey)
    mergedChildren.push(replacement)
  }

  for (const materializedChild of input.materializedChildren) {
    const provenanceKey = createEmbeddedPuppyOccurrenceProvenanceKey(materializedChild)
    if (!provenanceKey || !consumedMaterializedProvenances.has(provenanceKey)) {
      mergedChildren.push(materializedChild)
    }
  }

  return mergedChildren
}
